import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import ServiceBox from '@/scopes/checkout/components/services/ServiceBox'
import {
  Banner,
  ButtonLoaderIcon,
  countryData,
  PhoneField,
  space,
  SpaceBetween,
  tokPx,
} from '@sunwebgroup/ui-components'
import { Button, TextField, Typography } from '@sunwebgroup/ui-components/uds'

import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { styled } from 'styled-components'
import { useBookingDraftQuery, useEngineErrorContactInfo } from '../../domain/hooks'
import { mailPattern, namePattern } from '../helpers/Validations'
import { useEngineErrorContactInfoStore } from './store'

interface FormValues {
  name: string
  email: string
  phone: PhoneField
}

const FormWrapper = styled.form`
  gap: ${space('primitives_space_16')};
  display: flex;
  flex-direction: column;
`

const SubmitButton = styled(Button)`
  width: ${tokPx('primitives_area_size_240')};
  height: ${tokPx('primitives_space_48')};
`

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  > * {
    height: 100%;
    margin-top: ${space('primitives_space_64')};
    align-items: center;
    width: ${tokPx('primitives_area_size_720')};
  }
`

export function EngineError() {
  const intl = useIntl()
  const [isChecking, setIsChecking] = React.useState(false)
  const [isSent, setIsSet] = React.useState(false)
  const engineErrorConfig = useCMSPageField('Checkout.engineError')
  const { data } = useBookingDraftQuery()

  const { setValue, formState, control, handleSubmit, getFieldState } = useForm<FormValues>()
  const errorsTyped = formState.errors as Record<keyof FormValues, { message: string }>

  const { sendEngineErrorContactInfo } = useEngineErrorContactInfo()

  const engineErrorStore = useEngineErrorContactInfoStore(state => state.result)

  React.useEffect(() => {
    if (engineErrorStore === true) {
      setIsChecking(false)
      setIsSet(true)
    }
    else if (engineErrorStore === false) {
      setIsChecking(false)
    }
  }, [engineErrorStore])

  const onSubmit = (result: FormValues) => {
    setIsChecking(true)
    sendEngineErrorContactInfo({
      customerName: result.name,
      customerEmail: result.email,
      customerPhone: result.phone ? `+${result.phone.countryCode + result.phone.phoneNumber}` : '',
    })
  }

  const statusEmail = () => {
    const emailState = getFieldState('email', formState)

    if (!emailState.isTouched) {
      return 'default'
    }

    return emailState.invalid ? 'error' : 'success'
  }

  const statusName = () => {
    const nameState = getFieldState('name', formState)

    if (!nameState.isTouched) {
      return 'default'
    }

    return nameState.invalid ? 'error' : 'success'
  }

  const filteredCountry
    = (engineErrorConfig?.PhoneInputSettings
      && countryData.filter(country =>
        engineErrorConfig?.PhoneInputSettings.some(setting => setting.IsoCode === country.isoCode),
      ))
      || countryData

  return (
    <Wrapper>
      <ServiceBox title="" description="">
        <SpaceBetween spacing={space('primitives_space_20')} direction="y">
          {engineErrorStore === undefined && (
            <>
              <Banner $type="error">
                {engineErrorConfig && data?.error && (
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: replaceErrorMessage(engineErrorConfig.TextError, data.error.message),
                    }}
                  />
                )}
              </Banner>
              {engineErrorConfig && (
                <Typography dangerouslySetInnerHTML={{ __html: engineErrorConfig.TextDescription }} />
              )}
              <FormWrapper onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: true,
                    pattern: {
                      value: namePattern,
                      message: intl.formatMessage({ id: 'checkout_steps_engine_error_name_invalid' }),
                    },
                  }}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={intl.formatMessage({ id: 'checkout_steps_engine_error_name' })}
                      status={statusName()}
                      feedbackText={
                        errorsTyped.name?.message
                        || intl.formatMessage({ id: 'checkout_steps_engine_error_name_invalid' })
                      }
                      onBlur={(e) => {
                        field.onBlur()
                        const newValue = e.target.value
                        setValue('name', newValue, {
                          shouldValidate: true,
                        })
                      }}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: true,
                    pattern: {
                      value: mailPattern,
                      message: intl.formatMessage({ id: 'checkout_steps_engine_error_mail_invalid' }),
                    },
                  }}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      type="email"
                      status={statusEmail()}
                      feedbackText={intl.formatMessage({ id: 'checkout_steps_engine_error_mail_invalid' })}
                      label={intl.formatMessage({ id: 'checkout_steps_engine_error_mail' })}
                      {...field}
                      onBlur={(e) => {
                        field.onBlur()
                        const newValue = e.target.value
                        setValue('email', newValue, {
                          shouldValidate: true,
                        })
                      }}
                    />
                  )}
                />
                <div>
                  <Typography>{intl.formatMessage({ id: 'checkout_steps_engine_error_phone' })}</Typography>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <PhoneField
                        countries={filteredCountry}
                        id="phone"
                        value={field.value?.phoneNumber || ''}
                        countryCode={filteredCountry[0]?.code || ''}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                {!isSent && (
                  <SubmitButton $type="primary" type="submit" disabled={isChecking}>
                    {isChecking
                      ? (
                          <ButtonLoaderIcon />
                        )
                      : (
                          intl.formatMessage({ id: 'checkout_steps_engine_error_submit_contact_button' })
                        )}
                  </SubmitButton>
                )}
              </FormWrapper>
            </>
          )}
          {engineErrorStore === false && (
            <Banner $type="error">
              <Typography>{engineErrorConfig?.ContactFormResponseNok}</Typography>
            </Banner>
          )}
          {engineErrorStore === true && (
            <Banner $type="success">
              <Typography>{engineErrorConfig?.ContactFormResponseOk}</Typography>
            </Banner>
          )}
        </SpaceBetween>
      </ServiceBox>
    </Wrapper>
  )
}

function replaceErrorMessage(errorConfigDescription: string, errorMessage: string) {
  return errorConfigDescription.replace('{ERROR_MESSAGE}', errorMessage || '')
}
